import { Button, Flex, Space, TableProps, Tag } from 'antd'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useWriteOffs } from '@/api/write-off'
import AddIcon from '@/assets/icons/add-icon'
import DeleteIcon from '@/assets/icons/delete-icon'
import RemoveRedEyeIcon from '@/assets/icons/remove-red-eye-icon'
import DataTable from '@/components/data-table'
import FilterTableSimpleInput from '@/components/table-filters/simple-input-table-filter'
import TitleSection from '@/components/title-section'
import useFilterSearchParams from '@/hooks/useFilterSearchParams'
import { AssetInsightWriteOff } from '@/types/write-off'

function AssetInsightWriteOffPage() {
  const navigate = useNavigate()
  const [
    { page, pageSize, query, sort, order, searchParams },
    setSearchParams,
  ] = useFilterSearchParams()

  const { data, isFetching, isPlaceholderData, isPending } = useWriteOffs({
    page,
    pageSize,
    sorted: sort ? [{ sort, order }] : [{ sort: 'createdAt', order: 'DESC' }],
    filtered: [
      {
        id: 'contract.contractTitle',
        value: query || '',
        operator: 'global',
      },
    ],
  })
  const columns: TableProps<AssetInsightWriteOff>['columns'] = [
    {
      key: 'rowNumber',
      title: 'No',
      render: (_value, _record, index) => {
        return `${(page - 1) * pageSize + index + 1}.`
      },
    },
    {
      dataIndex: ['contract', 'contractTitle'],
      title: 'Contract Name',
      sorter: true,
    },
    {
      dataIndex: ['manager', 'name'],
      title: 'PIC Manager',
      sorter: true,
    },
    {
      dataIndex: 'date',
      title: 'Date',
      sorter: true,
      render(value) {
        return value ? dayjs(value).format('DD-MMM-YYYY') : '-'
      },
    },
    {
      dataIndex: 'items',
      title: 'Total Asset',
      sorter: true,
      render(value) {
        return value.length
      },
    },
    {
      dataIndex: 'status',
      title: 'Status',
      sorter: true,
      render: (value) => {
        let color = ''
        let text = ''
        switch (value) {
          case 'pending':
            color = 'warning'
            text = 'Pending'
            break
          case 'approved':
            color = 'success'
            text = 'Approved'
            break
          case 'rejected':
            color = 'error'
            text = 'Rejected'
            break

          default:
            break
        }
        return <Tag color={color}>{text}</Tag>
      },
    },
    {
      dataIndex: 'id',
      title: 'Action',
      render(value, record) {
        return (
          <Space>
            <Button
              type="primary"
              size="middle"
              icon={<RemoveRedEyeIcon width={20} height={20} />}
              onClick={() => navigate(`/asset-insight/write-off/${value}`)}
            />
            <Button
              type="primary"
              size="middle"
              danger
              disabled={record.status !== 'pending'}
              icon={<DeleteIcon width={20} height={20} />}
              onClick={() => {}}
            />
          </Space>
        )
      },
    },
  ]

  return (
    <div>
      <TitleSection
        title="Write Off"
        fontWeight={800}
        actions={[
          <Button
            type="primary"
            key="add"
            icon={<AddIcon width={20} height={20} />}
            onClick={() => navigate('/asset-insight/write-off/add-new')}
          >
            Add New Request
          </Button>,
        ]}
      />
      <Flex vertical gap={24}>
        <FilterTableSimpleInput
          disabled={isFetching}
          onChange={(value) => {
            if (value) {
              searchParams.set('query', value)
            } else {
              searchParams.delete('query')
            }
            setSearchParams(searchParams)
          }}
          value={query}
        />

        <DataTable<AssetInsightWriteOff>
          rowSelectable={false}
          loading={isPlaceholderData || isPending}
          dataSource={data?.data || []}
          columns={columns}
          total={data?.total}
        />
      </Flex>
    </div>
  )
}

export default AssetInsightWriteOffPage
